/**
 * Centralized API Client for Explanatum
 * 
 * Provides consistent:
 * - Authentication (JWT tokens)
 * - Error handling
 * - Retry logic
 * - Timeout handling
 * - Response validation
 * 
 * Usage:
 * import { apiClient } from '@/lib/apiClient';
 * const data = await apiClient.post('/business-ideas/generate', { params });
 */

import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';

// Get JWT access token from Supabase session
const getAuthToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('Error getting Supabase auth token:', e);
    return '';
  }
};

interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  requiresAuth?: boolean;
}

interface ApiError extends Error {
  status?: number;
  statusText?: string;
  data?: any;
}

class ApiClient {
  private baseURL: string;
  private defaultTimeout: number;
  private defaultRetries: number;

  constructor() {
    this.baseURL = `${ENV.API_URL}/api`;
    this.defaultTimeout = ENV.API_TIMEOUT || 30000;
    this.defaultRetries = ENV.MAX_RETRIES || 3;
  }

  /**
   * Make an authenticated API request with retry logic
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      requiresAuth = true,
      headers = {},
      ...fetchOptions
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    
    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers as Record<string, string>,
    };

    // Add auth token if required
    if (requiresAuth) {
      const token = await getAuthToken();
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('No auth token available for authenticated request');
      }
    }

    let lastError: ApiError | null = null;
    let attempt = 0;

    while (attempt < retries) {
      attempt++;
      
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...fetchOptions,
          headers: requestHeaders,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle non-OK responses
        if (!response.ok) {
          const error = new Error() as ApiError;
          error.status = response.status;
          error.statusText = response.statusText;

          try {
            error.data = await response.json();
            error.message = error.data.message || error.data.error || `HTTP ${response.status}: ${response.statusText}`;
          } catch {
            const textError = await response.text();
            error.message = textError || `HTTP ${response.status}: ${response.statusText}`;
          }

          // Don't retry client errors (4xx), only server errors (5xx)
          if (response.status >= 400 && response.status < 500) {
            throw error;
          }

          lastError = error;
          
          // Wait before retry with exponential backoff
          if (attempt < retries) {
            await this.delay(ENV.RETRY_DELAY * Math.pow(2, attempt - 1));
            continue;
          }
          
          throw error;
        }

        // Success - parse and return response
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          return await response.json();
        }
        
        return await response.text() as T;

      } catch (error: any) {
        // Handle timeout
        if (error.name === 'AbortError') {
          const timeoutError = new Error(`Request timeout after ${timeout}ms`) as ApiError;
          lastError = timeoutError;
          
          if (attempt < retries) {
            await this.delay(ENV.RETRY_DELAY * Math.pow(2, attempt - 1));
            continue;
          }
          
          throw timeoutError;
        }

        // Handle network errors
        if (error instanceof TypeError && error.message.includes('fetch')) {
          const networkError = new Error(`Network error: Unable to reach ${url}. Please check your connection.`) as ApiError;
          lastError = networkError;
          
          if (attempt < retries) {
            await this.delay(ENV.RETRY_DELAY * Math.pow(2, attempt - 1));
            continue;
          }
          
          throw networkError;
        }

        // Rethrow if it's already an ApiError
        if (error.status) {
          throw error;
        }

        // Unknown error
        lastError = error;
        if (attempt < retries) {
          await this.delay(ENV.RETRY_DELAY * Math.pow(2, attempt - 1));
          continue;
        }
        
        throw error;
      }
    }

    // If we exhausted all retries
    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * Delay utility for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types for consumers
export type { ApiError, RequestOptions };
